import {
    Box,
    Card,
    CardHeader,
    CardMedia,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputBase,
    MenuItem,
    Select,
    Typography,
    alpha,
} from '@mui/material';
import { useState } from 'react';
import TemplateCard from './TemplateCard';
import { CloseRounded } from '@mui/icons-material';
import useEditorProps from '@hooks/useEditorProps';
import saveTemplateHelperEn from '@assets/saveTemplateHelperEn.png';
import saveTemplateHelperHe from '@assets/saveTemplateHelperHe.png';
import { useTranslation } from 'react-i18next';
import { Transforms } from 'slate';
import { useSlate } from 'slate-react';

function DefaultTemplateDialog({ open, onClose }) {
    const { t, i18n } = useTranslation();
    const [templateSelection, setTemplateSelection] = useState('public');
    const { publicTemplates, savedTemplates, designs } = useEditorProps();

    const templatsMap = {
        public: publicTemplates,
        private: savedTemplates,
        designs: designs,
    };

    const templates = templatsMap[templateSelection];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            // fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    width: 740,
                    height: '100%',
                },
            }}
        >
            <DialogTitle variant="h5">
                <Typography
                    variant="inherit"
                    textAlign="center"
                    gutterBottom
                    marginBottom={2}
                >
                    {t('Choose a Template')}
                </Typography>
                <IconButton
                    onClick={onClose}
                    sx={{ position: 'absolute', top: 4, right: 4 }}
                    size="small"
                >
                    <CloseRounded fontSize="large" />
                </IconButton>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1">
                        {t('Template Gallery')}
                    </Typography>
                    <Select
                        input={<InputBase />}
                        value={templateSelection}
                        onChange={e => setTemplateSelection(e.target.value)}
                        sx={{
                            '&:hover': {
                                backgroundColor: theme =>
                                    alpha(
                                        theme.palette.primary.main,
                                        theme.palette.action.hoverOpacity,
                                    ),
                            },
                            '& > [aria-expanded="true"]': {
                                backgroundColor: theme =>
                                    alpha(
                                        theme.palette.primary.main,
                                        theme.palette.action.selectedOpacity,
                                    ),
                            },
                            '& .MuiSelect-select': {
                                pl: 2,
                            },
                            borderRadius: 1,
                        }}
                    >
                        <MenuItem value="public">
                            {t('Public Templates')}
                        </MenuItem>
                        <MenuItem value="private">{t('My Templates')}</MenuItem>
                        <MenuItem value="designs">{t('My Designs')}</MenuItem>
                    </Select>
                </Box>
            </DialogTitle>
            <DialogContent
                sx={{
                    overflowY: 'scroll',
                    pr: 2,
                }}
            >
                {templates.length === 0 && (
                    <Box
                        height="100%"
                        width="100%"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        textAlign="center"
                    >
                        <Box width={500}>
                            <Typography variant="h5" gutterBottom>
                                {t('Save Your First Template')}
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                color="text.secondary"
                            >
                                {t(
                                    'You can save your current layout as a template for future use.',
                                )}
                            </Typography>
                            <Box
                                sx={{
                                    marginY: 4,
                                    marginX: 12,
                                    borderRadius: 2,
                                    border: 1,
                                    borderColor: 'divider',
                                    overflow: 'hidden',
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    image={
                                        i18n.language === 'he'
                                            ? saveTemplateHelperHe
                                            : saveTemplateHelperEn
                                    }
                                    sx={{ overflow: 'hidden' }}
                                />
                            </Box>
                        </Box>
                    </Box>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',

                        rowGap: 4,
                        columnGap: 2,
                    }}
                >
                    {templates.map((template, index) => (
                        <TemplateCard
                            key={index}
                            template={template}
                            onClose={onClose}
                            type={
                                templateSelection === '1' ? 'public' : 'private'
                            }
                        />
                    ))}
                </Box>
            </DialogContent>
        </Dialog>
    );
}

const TemplateDialog = ({ open, onClose }) => {
    const { SelectTemplate } = useEditorProps();
    const editor = useSlate();

    const handleSelect = template => {
        Transforms.removeNodes(editor, { at: [0] });
        Transforms.insertNodes(editor, structuredClone(template), {
            at: [0],
        });
        onClose();
    };

    return (
        <SelectTemplate open={open} onClose={onClose} onSelect={handleSelect} />
    );
};
export default TemplateDialog;
